/**
 * HmSaleDetail — formatka dokumentu sprzedaży (PODGLĄD).
 *
 * Układ 1:1 z formatką desktopowego Handlu: dwie kolumny nagłówka (typ i kontrahent po lewej,
 * płatność i daty po prawej), pod nimi zakładki z pozycjami i danymi adresowymi, na dole pasek
 * z sumami. Dzięki temu księgowa widzi dokument tam, gdzie go zna z Symfonii. Klocki formatki
 * (pole, sekcja, zakładki, pasek sum, wydruk) są wspólne dla czterech podglądów.
 *
 * ZNANE OGRANICZENIE: korekty (typ `FKS`) nie mają szczegółu pod tą trasą WebAPI — Symfonia
 * obsługuje je osobnym zasobem. Zamiast pustego ekranu pokazujemy wtedy komunikat wprost.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import type { HmSaleDetail as HmSaleDetailDto } from '@saas/shared';
import { useQuery } from '@tanstack/react-query';

import { AdHocBuyerPreview } from '@/components/handel-magazyn/AdHocBuyerPreview';
import {
  AddressBlock,
  day,
  DetailError,
  DetailHeader,
  DetailTabBody,
  DetailTabs,
  Field,
  PdfPreviewModal,
  Section,
  TotalsBar,
  useDocumentTabLabel,
  usePdfPreview,
} from '@/components/handel-magazyn/detail-parts';
import { FiscalizeButton } from '@/components/handel-magazyn/FiscalizeButton';
import { FiscalPrintButton } from '@/components/handel-magazyn/FiscalPrintButton';
import { HmApprovalSection } from '@/components/handel-magazyn/HmApprovalSection';
import { InvoiceKindBadge, KsefStatus } from '@/components/handel-magazyn/ksef-cell';
import {
  PrintDocumentModal,
  printSettingsToParams,
} from '@/components/handel-magazyn/PrintDocumentModal';
import { api } from '@/lib/api';
import { useActiveCompanyScope } from '@/lib/use-active-company';

const TABS = ['positions', 'address', 'extra'] as const;
type Tab = (typeof TABS)[number];

export function HmSaleDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const companyId = useActiveCompanyScope().companyId;
  const [tab, setTab] = useState<Tab>('positions');
  const pdf = usePdfPreview(`/handel-magazyn/sales/${id}/pdf?companyId=${companyId}`);
  const [printOpen, setPrintOpen] = useState(false);

  const query = useQuery({
    queryKey: ['handel-magazyn', 'sale', id, companyId],
    enabled: !!id && !!companyId,
    retry: false,
    queryFn: async (): Promise<HmSaleDetailDto> => {
      const res = await api.get<HmSaleDetailDto>(
        `/handel-magazyn/sales/${id}?companyId=${companyId}`,
      );
      return res.data;
    },
  });

  // Numer dokumentu znamy dopiero po odpowiedzi — podmieniamy etykiete karty
  // z ogolnej na konkretny numer.
  useDocumentTabLabel(`/handel-magazyn/sprzedaz/${id}`, query.data?.documentNumber);

  if (query.isLoading) {
    return <div className="p-4 text-sm text-text-secondary">{t('common.loading')}</div>;
  }
  if (query.error) return <DetailError error={query.error} />;

  const doc = query.data!;

  return (
    <div className="flex h-full flex-col p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <DetailHeader
            documentNumber={doc.documentNumber}
            onPrint={() => setPrintOpen(true)}
            printLoading={pdf.loading}
          />
        </div>
        {/* Dwa różne przyciski, celowo obok siebie i celowo różnie nazwane:
            „Drukuj paragon" faktycznie DRUKUJE (przez naszą usługę przy drukarce u klienta),
            „Fiskalizuj" tylko ODNOTOWUJE numer paragonu wydrukowanego gdzie indziej. Zlanie ich
            w jedno kazałoby zgadywać, czy klik wypluje papier. */}
        <div className="mt-1 flex shrink-0 gap-2">
          <FiscalPrintButton
            documentId={doc.id}
            companyId={companyId}
            inBuffer={doc.inBuffer}
            fiscalized={doc.fiscalized}
            buyerTaxId={doc.buyerAddress?.taxId ?? null}
            onDone={() => void query.refetch()}
          />
          <FiscalizeButton
            documentId={doc.id}
            companyId={companyId}
            inBuffer={doc.inBuffer}
            fiscalized={doc.fiscalized}
            onDone={() => void query.refetch()}
          />
        </div>
      </div>

      {/* Obieg akceptacji. Dokument żyje w Symfonii — u nas powstaje wyłącznie rezerwacja
          identyfikatora, i to dopiero przy wysłaniu do akceptacji. */}
      <div className="mb-3">
        <HmApprovalSection kind="sale" externalId={doc.id} companyId={companyId} />
      </div>

      <div className="grid grid-cols-1 gap-6 rounded-md border border-border-primary bg-bg-secondary p-4 lg:grid-cols-2">
        <div className="space-y-4">
          <Section title={t('tenant.handelMagazyn.detail.documentType')}>
            <Field label={t('tenant.handelMagazyn.columns.typeCode')} value={doc.typeCode} />
            <Field label={t('tenant.handelMagazyn.detail.series')} value={doc.series} />
            <Field
              label={t('tenant.handelMagazyn.columns.status')}
              value={
                doc.inBuffer
                  ? t('tenant.handelMagazyn.status.buffer')
                  : t('tenant.handelMagazyn.status.issued')
              }
            />
          </Section>

          <Section title={t('tenant.handelMagazyn.columns.contractor')}>
            {/* Nabywca JEDNOKROTNY nie ma kartoteki, więc jego dane widać tylko tu — nazwa
                w polu, resztę pod przyciskiem, dokładnie jak w oknie Symfonii. */}
            {doc.buyerIsAdHoc ? (
              <AdHocBuyerPreview name={doc.buyerName} address={doc.buyerAddress} />
            ) : (
              <>
                <Field label={t('tenant.handelMagazyn.detail.name')} value={doc.buyerName} />
                <Field
                  label={t('tenant.handelMagazyn.detail.taxId')}
                  value={doc.buyerAddress?.taxId ?? null}
                />
              </>
            )}
          </Section>
        </div>

        <div className="space-y-4">
          <Section title={t('tenant.handelMagazyn.detail.payment')}>
            <Field label={t('tenant.handelMagazyn.detail.currency')} value={doc.currency} />
            <Field
              label={t('tenant.handelMagazyn.detail.settled')}
              value={
                doc.settled
                  ? t('tenant.handelMagazyn.detail.yes')
                  : t('tenant.handelMagazyn.detail.no')
              }
            />
          </Section>

          <Section title={t('tenant.handelMagazyn.detail.dates')}>
            <Field label={t('tenant.handelMagazyn.columns.issueDate')} value={day(doc.issueDate)} />
            <Field label={t('tenant.handelMagazyn.columns.saleDate')} value={day(doc.saleDate)} />
            <Field
              label={t('tenant.handelMagazyn.columns.maturityDate')}
              value={day(doc.maturityDate)}
            />
          </Section>
        </div>
      </div>

      {/* KSeF osobną sekcją, nie wśród dat: numer KSeF bywa potrzebny do skopiowania, a status
          jest KODEM Symfonii — obie rzeczy zasługują na własne miejsce zamiast doklejenia. */}
      <div className="mt-3 rounded-md border border-border-primary bg-bg-secondary p-4">
        <h2 className="mb-2 text-sm font-semibold text-text-primary">
          {t('tenant.handelMagazyn.ksef.section')}
        </h2>
        <div className="grid gap-3 md:grid-cols-3">
          <div className="grid grid-cols-[110px_1fr] items-center gap-2">
            <span className="text-sm text-text-secondary">
              {t('tenant.handelMagazyn.ksef.kindColumn')}
            </span>
            <div>
              <InvoiceKindBadge kind={doc.invoiceKind} />
            </div>
          </div>
          <div className="grid grid-cols-[110px_1fr] items-center gap-2">
            <span className="text-sm text-text-secondary">
              {t('tenant.handelMagazyn.ksef.statusColumn')}
            </span>
            <div className="rounded-md border border-border-primary bg-bg-tertiary px-2.5 py-1.5 text-sm">
              <KsefStatus code={doc.ksefStatusCode} kind={doc.invoiceKind} />
            </div>
          </div>
          <Field
            label={t('tenant.handelMagazyn.ksef.issueDateColumn')}
            value={doc.ksefIssueDate ? day(doc.ksefIssueDate) : null}
          />
        </div>
        <div className="mt-2">
          <Field label={t('tenant.handelMagazyn.ksef.numberColumn')} value={doc.ksefNumber} />
        </div>
      </div>

      <DetailTabs tabs={TABS} active={tab} onChange={setTab} />

      <DetailTabBody>
        {tab === 'positions' && (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border-primary text-left text-xs uppercase tracking-wide text-text-tertiary">
                <th className="px-2 py-1.5 font-medium">
                  {t('tenant.handelMagazyn.detail.pos.no')}
                </th>
                <th className="px-2 py-1.5 font-medium">
                  {t('tenant.handelMagazyn.detail.pos.code')}
                </th>
                <th className="px-2 py-1.5 font-medium">
                  {t('tenant.handelMagazyn.detail.pos.description')}
                </th>
                <th className="px-2 py-1.5 text-right font-medium">
                  {t('tenant.handelMagazyn.detail.pos.quantity')}
                </th>
                <th className="px-2 py-1.5 font-medium">
                  {t('tenant.handelMagazyn.detail.pos.unit')}
                </th>
                <th className="px-2 py-1.5 font-medium">
                  {t('tenant.handelMagazyn.detail.pos.vat')}
                </th>
                <th className="px-2 py-1.5 text-right font-medium">
                  {t('tenant.handelMagazyn.detail.pos.price')}
                </th>
                <th className="px-2 py-1.5 text-right font-medium">
                  {t('tenant.handelMagazyn.columns.net')}
                </th>
                <th className="px-2 py-1.5 text-right font-medium">
                  {t('tenant.handelMagazyn.columns.gross')}
                </th>
              </tr>
            </thead>
            <tbody>
              {doc.positions.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-2 py-6 text-center text-text-tertiary">
                    {t('tenant.handelMagazyn.detail.noPositions')}
                  </td>
                </tr>
              )}
              {doc.positions.map((p) => (
                <tr key={p.id} className="border-b border-border-primary/60">
                  <td className="px-2 py-1.5 text-text-tertiary">{p.no}</td>
                  <td className="px-2 py-1.5 font-mono text-xs text-text-primary">
                    {p.productCode}
                  </td>
                  <td className="px-2 py-1.5 text-text-primary">{p.description}</td>
                  <td className="px-2 py-1.5 text-right text-text-primary">{p.quantity}</td>
                  <td className="px-2 py-1.5 text-text-secondary">{p.unit}</td>
                  <td className="px-2 py-1.5 text-text-secondary">{p.vatRateCode ?? '—'}</td>
                  <td className="px-2 py-1.5 text-right text-text-secondary">{p.priceValue}</td>
                  <td className="px-2 py-1.5 text-right text-text-primary">{p.netValue}</td>
                  <td className="px-2 py-1.5 text-right font-medium text-text-primary">
                    {p.grossValue}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {tab === 'address' && (
          <div className="grid gap-6 md:grid-cols-2">
            <AddressBlock
              title={t('tenant.handelMagazyn.detail.buyerAddress')}
              address={doc.buyerAddress}
            />
            <AddressBlock
              title={t('tenant.handelMagazyn.detail.recipientAddress')}
              address={doc.recipientAddress}
            />
          </div>
        )}

        {tab === 'extra' && (
          <div className="space-y-2">
            <Field label={t('tenant.handelMagazyn.detail.description')} value={doc.description} />
            <Field label={t('tenant.handelMagazyn.detail.note')} value={doc.note} />
          </div>
        )}
      </DetailTabBody>

      <TotalsBar
        items={[
          { label: t('tenant.handelMagazyn.columns.net'), value: doc.netValue },
          { label: t('tenant.handelMagazyn.columns.vat'), value: doc.vatValue },
          {
            label: t('tenant.handelMagazyn.columns.gross'),
            value: `${doc.grossValue} ${doc.currency}`,
            strong: true,
          },
        ]}
      />

      <PdfPreviewModal url={pdf.url} title={doc.documentNumber} onClose={pdf.close} />
      <PrintDocumentModal
        open={printOpen}
        kind="sales"
        companyId={companyId}
        documentNumber={doc.documentNumber}
        onClose={() => setPrintOpen(false)}
        onPrint={(settings) => {
          setPrintOpen(false);
          void pdf.show(printSettingsToParams(settings));
        }}
      />
    </div>
  );
}

export default HmSaleDetail;
